import React, { Component, ErrorInfo, ReactNode } from "react";
import { recordCrashTombstone } from "@/services/crash-reporter";
import { logAndFlush, flushQueue } from "@/services/batch-logger";
import { ErrorScreen } from "@/components/ErrorScreen";

export interface IErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

export interface IErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Standard Error Boundary for the Memories application.
 * Catches uncaught runtime render errors, writes a persistent crash tombstone,
 * flushes error logs to Google Cloud Logging on the server, and renders the
 * standard themed ErrorScreen.
 */
export default class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
  public state: IErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught error caught by boundary:", error, errorInfo);

    // 1. Record persistent crash tombstone to disk
    recordCrashTombstone(
      error,
      false,
      `ErrorBoundary: ${errorInfo.componentStack || "no-component-stack"}`
    );

    // 2. Queue error log AND immediately flush to remote server BEFORE error presentation
    logAndFlush(
      "error",
      `[ErrorBoundary] Uncaught React exception: ${error?.message || String(error)}`,
      `${error?.stack || "No JS stack"}\nComponent Stack: ${errorInfo.componentStack || "No component stack"}`
    );

    // Trigger explicit queue flush to guarantee delivery
    flushQueue().catch((err) => {
      console.warn("[ErrorBoundary] Failed to flush log queue immediately:", err);
    });

    this.setState({ hasError: true, error, errorInfo });
  }

  private handleReset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorScreen
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

